/**
 * wireGlobalListeners — popstate handler + cross-tab storage events.
 *
 * Called once after the shell mounts and the initial dispatch has run.
 *
 * Handles:
 *   - popstate: re-dispatch on browser back/forward
 *   - storage 'activeOrg': switchOrg (cross-tab)
 *   - storage 'activeWorkspace': switchWorkspace (cross-tab)
 *   - storage 'activeWorkspaceProject': switchWorkspaceProject (cross-tab)
 *   - storage 'logout': clear all state + redirect to /login
 *   - storage 'themeMode': update <html data-theme> + state
 *   - storage 'lang': call setLang with skipPersist (avoid echo loop)
 *
 * Not here: the keyboard and mailto: clicks. The shell's one keydown listener
 * is the shell root's declared `onDocumentKeydown`, and the in-app mail
 * composer for a plain mailto: click is its declared `onDocumentClick`
 * (app.js, functions/mailtoComposeDefault.js) — never a raw listener (FA503).
 */

import { dispatchUrl } from './dispatch.js'
import { isCustomDomainHost } from './functions/customDomainHost.js'
import _wsShared from '@symbo-ls/workspace-shared'
const switchOrg = (...args) => _wsShared.functions.switchOrg(...args)
const switchWorkspace = (...args) => _wsShared.functions.switchWorkspace(...args)
const switchWorkspaceProject = (...args) => _wsShared.functions.switchWorkspaceProject(...args)
const logout = (...args) => _wsShared.functions.logout(...args)
const normalizeThemeMode = (...args) => _wsShared.functions.normalizeThemeMode(...args)
const shellIsMwtEnabled = (...args) => _wsShared.functions.isMwtEnabled(...args)
/**
 * @param {object} shell           - root shell element from create()
 * @param {object} opts
 * @param {object} opts.apps       - app roster (Phase 7 stubs or Phase 8 manifests)
 * @param {string} opts.fallbackRoute
 */
export const wireGlobalListeners = (shell, { apps, fallbackRoute = '/' } = {}) => {
  // --- popstate ---
  window.addEventListener('popstate', async () => {
    const path = window.location.pathname + window.location.search
    await dispatchUrl(shell, path)
  })

  // The keyboard is NOT wired here: the shell's one keydown listener is the
  // shell root's declared `onDocumentKeydown` (app.js — registry + guard in
  // functions/shortcuts.js), so DOMQL owns its lifecycle (FA503).

  // mailto: clicks are NOT wired here: the in-app composer is the DEFAULT
  // action of a plain mailto click, the shell root's declared
  // `onDocumentClick` (app.js — functions/mailtoComposeDefault.js). The raw
  // CAPTURE listener that lived here ran before every surface's own handler
  // and stopped the click, so a surface could never keep its mailto link.

  // --- cross-tab storage events ---
  window.addEventListener('storage', async (e) => {
    // Null new value means the key was removed — ignore.
    if (e.newValue === null) return

    const state = shell.state?.root || shell.state
    // Lazy-resolve SDK + queryClient from context so we don't need to pass
    // them as parameters. The shell's context is attached by bootShell.
    const sdk = shell.context?.sdk || null
    const queryClient = shell.context?.queryClient || null

    // Note: no `activeOrg` cross-tab branch — active org is sourced
    // from Mongo (`User.activeOrganization`) and synced cross-tab via
    // socket.io `active-org-changed` in `subscribeUserEvents` (wired
    // by `workspace/app.js shellApp.onCreate`), not localStorage.

    if (e.key === 'activeWorkspace' && state?.workspace?.id !== e.newValue) {
      // I6 (§6c): under MWT this tab is PINNED to its own workspace from boot —
      // never let another tab's switch (a shared-key `storage` event) drag it off.
      // Gate on the flag itself, NOT the URL: the `/w/<slug>` prefix is absent
      // during the pre-canonicalize boot window, so a URL check would leave that
      // window unguarded (the boot-window race). No-op while the flag is off.
      if (shellIsMwtEnabled()) return
      // WHITELABEL-STRICT-TENANT-SCOPE-1 — a white-label host serves ONE
      // workspace; no other tab may switch this one to a different workspace.
      if (isCustomDomainHost()) return
      if (sdk && queryClient) {
        await switchWorkspace(state, sdk, queryClient, e.newValue)
      }
    }

    if (e.key === 'activeWorkspaceProject' && state?.workspaceProject?.id !== e.newValue) {
      if (sdk && queryClient) {
        await switchWorkspaceProject(state, sdk, queryClient, e.newValue)
      }
    }

    if (e.key === 'logout') {
      // Another tab signed out — mirror the logout locally.
      if (sdk && queryClient) {
        await logout(state, sdk, queryClient)
      } else {
        window.history.replaceState(null, '', '/login')
      }
    }

    if (e.key === 'themeMode') {
      // changeGlobalTheme flips data-theme (scratch resolves 'auto' and
      // attaches its prefers-color-scheme listener). Theme visuals are
      // '@dark'/'@light' CSS, and the canvas-bg cookie var re-syncs via
      // the document-level data-theme watcher — nothing else to do here.
      //
      // Dynamic-imported: this only fires on a cross-tab 'storage' event
      // (another tab flipped the theme), never on the boot path, so a
      // static top-level `import { changeGlobalTheme } from 'smbls'` here
      // would drag the whole framework bundle into the eager entry chunk
      // for a listener branch that may never fire in a session. index.js
      // already warms this exact chunk (`void import('smbls')`) at entry,
      // so this resolves instantly once it's actually needed.
      const mode = normalizeThemeMode(e.newValue)
      const { changeGlobalTheme } = await import('smbls')
      changeGlobalTheme(mode)
      if (state) state.themeMode = mode
    }

    if (e.key === 'lang' && e.newValue && state?.lang !== e.newValue) {
      // Cross-tab language sync. This read used to be
      // `shell.context?.methods?.setLang`, and it NEVER resolved: `setLang`
      // is exported from the shared `functions/` barrel, never from
      // `methods/`, so the branch always fell through to the bare
      // `state.lang = …` below. That assignment changes the state field and
      // nothing else — no bundle load, no <html lang>/dir, no polyglot
      // switch — so a language change in one tab left every other tab
      // rendering the old language with the new code in its state.
      //
      // `el.call` is the right resolver: smbls's methods.js `call()` walks
      // context.methods -> context.functions -> context.utils and invokes
      // with `this` bound to the element. That binding is now REQUIRED —
      // `setLang` reads the element to reach `context.polyglot` and the root
      // state, and it is a function declaration precisely so it can.
      if (typeof shell?.call === 'function') {
        await shell.call('setLang', e.newValue, { skipPersist: true })
      } else if (state) {
        state.lang = e.newValue
      }
    }
  })
}
